import { getCookie, setCookie, updateCookie, text_truncate, getBrowserType } from '../core/utils.js';
// import { setCampaignInfo } from '../components/campaign-info.js';

var code = '',
    ccode = '',
    leads_referrer = document.referrer;

/*
  waba-trigger.js

  Ringkasan:
  Script ini menangani klik pada elemen dengan class `.cta-waba` (CTA WhatsApp/WABA) dengan tujuan:
  1) Loader tampil instan agar UX jelas dan mencegah user klik berkali-kali.
  2) WhatsApp dibuka di TAB BARU (bukan tab yang sama) dengan cara yang paling tahan popup-blocker.
  3) Tracking tersimpan dengan reliabel:
     - Prioritas: simpan ke DB via admin-ajax (lebih stabil dibanding REST wp-json yang kadang 403).
     - Fallback: kirim ke Google Sheets via Apps Script jika DB gagal.
  4) Mengatasi lingkungan legacy:
     - Beberapa template/footer lama masih punya handler klik inline yang melakukan `form.submit()`
       untuk redirect tab yang sama.
     - Ada kemungkinan script global memicu klik sintetis (programmatic click) setelah delay.

  Masalah yang pernah terjadi & mitigasinya di file ini:
  - Loader tidak muncul => ditangani dengan display:flex !important + fallback overlay injection.
  - Double event di mobile (pointerup/touchend/click) => ditangani dengan dedupe.
  - "Double trigger" WABA (tab lama ikut redirect setelah beberapa detik) =>
    ditangani dengan kombinasi:
    a) stopPropagation/stopImmediatePropagation untuk memblok handler legacy klik
    b) guard programmatic submit pada form shortener
    c) blok event sintetis menggunakan `Event.isTrusted === false`

  Catatan cara baca:
  - Bagian "click path" (yang harus cepat) dioptimasi: cache cookie, cache URL object,
    dan menunda kerja berat (network, FormData/JSON) ke idle/after paint.
*/

export function waba() {

  // Prevent double-binding if this function is invoked multiple times.
  if (window.__jurnalWabaBound && window.__jurnalWabaHandlerAttached) return;

  var curr_lang = document.documentElement.getAttribute('lang') ? document.documentElement.getAttribute('lang') : 'id';

  var firstlanding = '';

  if(getCookie('user_journey')) {
    var journey_ = getCookie('user_journey');
    journey_ = journey_.split('|');
    if(journey_.length > 1) {
      firstlanding = journey_[0].replace('J','');
    }
  }

  const LOADING_FALLBACK_ID = 'jurnal-waba-loading-fallback';

  // Cache loading nodes so we don't query the whole DOM on every click.
  // Rebuild only if cache is empty (some templates may inject loader later).
  let _cachedLoadingEls = null;
  const getLoadingEls = () => {
    try {
      if (Array.isArray(_cachedLoadingEls) && _cachedLoadingEls.length) return _cachedLoadingEls;
      const live = document.getElementsByClassName('has-loading');
      _cachedLoadingEls = live && live.length ? Array.from(live) : [];
      return _cachedLoadingEls;
    } catch (e) {
      _cachedLoadingEls = [];
      return _cachedLoadingEls;
    }
  };

  // Some templates still include legacy WABA code that calls
  // `form_waba.submit()` to drive redirects in the current tab.
  // Programmatic `form.submit()` bypasses the `submit` event, so we guard it
  // by temporarily blocking submits for the hidden shortener form right after
  // a WABA click (we handle navigation ourselves via a new tab).
  const ensureFormSubmitGuard = () => {
    // Guard ini membungkus HTMLFormElement.prototype.submit.
    // Kenapa perlu?
    // - `form.submit()` itu programmatic dan TIDAK memicu event `submit`, jadi kita
    //   tidak bisa mengandalkan addEventListener('submit', ...).
    // - Legacy inline script melakukan `form_waba.submit()` setelah user klik `.cta-waba`.
    //   Akibatnya: tab yang sama bisa ikut redirect (bug kritikal).
    // - Solusi: selama window waktu tertentu setelah WABA klik, kita blok submit
    //   untuk form shortener tertentu.
    if (window.__jurnalWabaFormSubmitGuardInstalled) return;
    window.__jurnalWabaFormSubmitGuardInstalled = true;

    try {
      if (!window.HTMLFormElement || !window.HTMLFormElement.prototype) return;
      const originalSubmit = window.HTMLFormElement.prototype.submit;
      if (typeof originalSubmit !== 'function') return;

      // Avoid double-wrapping.
      if (originalSubmit.__jurnalWrapped) return;

      const wrapped = function() {
        try {
          const until = window.__jurnalBlockWabaFormSubmitUntil || 0;
          const now = Date.now();
          if (now < until) {
            const id = this && this.id ? String(this.id) : '';
            const name = this && this.getAttribute ? String(this.getAttribute('name') || '') : '';
            if (id === 'form_shortener_gclid' || name === 'form-shortener-gclid') {
              // Jika masih dalam window block, abaikan submit.
              // Navigasi sudah ditangani oleh handler utama (open new tab).
              return;
            }
          }
        } catch (e) {}
        return originalSubmit.apply(this, arguments);
      };
      wrapped.__jurnalWrapped = true;

      window.HTMLFormElement.prototype.submit = wrapped;
    } catch (e) {
      // no-op
    }
  };

  ensureFormSubmitGuard();

  const setDisplayImportant = (el, value) => {
    // Sama seperti file lain: paksa display dengan !important agar overlay benar-benar tampil.
    try {
      el.style.setProperty('display', value, 'important');
    } catch (e) {
      el.style.display = value;
    }
  };

  const ensureFallbackLoading = () => {
    // Fallback overlay khusus WABA:
    // Dipakai jika halaman tidak menyediakan `.has-loading`.
    // Ini membuat loader tetap tampil bahkan pada template yang minim.
    let el = document.getElementById(LOADING_FALLBACK_ID);
    if (el) return el;

    el = document.createElement('div');
    el.id = LOADING_FALLBACK_ID;
    el.setAttribute('aria-hidden', 'true');
    el.style.cssText = 'position:fixed;z-index:2147483001;top:0;left:0;width:100%;height:100%;background:#000;opacity:0.5;display:none;align-items:center;justify-content:center;';

    const spinner = document.createElement('div');
    spinner.style.cssText = 'width:60px;height:60px;border:6px solid rgba(255,255,255,0.35);border-top-color:#fff;border-radius:50%;animation:jurnalWabaSpin 0.9s linear infinite;';
    el.appendChild(spinner);

    // Inject keyframes once.
    if (!document.getElementById('jurnal-waba-loading-style')) {
      const style = document.createElement('style');
      style.id = 'jurnal-waba-loading-style';
      style.textContent = '@keyframes jurnalWabaSpin{to{transform:rotate(360deg)}}';
      document.head && document.head.appendChild(style);
    }

    (document.body || document.documentElement).appendChild(el);
    return el;
  };

  const showLoading = () => {
    // Prioritas: gunakan loader global jika ada (loader-guard.js)
    // supaya tampilan konsisten antar fitur.
    if (typeof window.__jurnalShowLoading === 'function') {
      window.__jurnalShowLoading();
      return;
    }
    const loadingEls = getLoadingEls();
    if (loadingEls && loadingEls.length) {
      for (let i = 0; i < loadingEls.length; i++) {
        setDisplayImportant(loadingEls[i], 'flex');
      }
      return;
    }
    setDisplayImportant(ensureFallbackLoading(), 'flex');
  };

  const hideLoading = () => {
    if (typeof window.__jurnalHideLoading === 'function') {
      window.__jurnalHideLoading();
      return;
    }
    const loadingEls = getLoadingEls();
    if (loadingEls && loadingEls.length) {
      for (let i = 0; i < loadingEls.length; i++) {
        setDisplayImportant(loadingEls[i], 'none');
      }
    }
    const fallback = document.getElementById(LOADING_FALLBACK_ID);
    if (fallback) setDisplayImportant(fallback, 'none');
  };

  const rafShowLoading = () => requestAnimationFrame(showLoading);
  const rafHideLoading = () => requestAnimationFrame(hideLoading);

  const scheduleSoon = (fn) => setTimeout(fn, 0);
  // Ensure the browser gets at least one paint opportunity before running work.
  // This helps the loader appear immediately after click on slow devices.
  const scheduleAfterPaint = (fn) => {
    // Pola: requestAnimationFrame -> setTimeout 0
    // Tujuan: memberi kesempatan browser melakukan paint 1 frame.
    // Ini penting agar overlay loader terlihat dulu sebelum kita melakukan kerja berat.
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => setTimeout(fn, 0));
    } else {
      setTimeout(fn, 0);
    }
  };
  const scheduleIdle = (fn) => {
    // Menunda pekerjaan non-kritis ke waktu idle untuk meningkatkan INP.
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: 1500 });
    } else {
      setTimeout(fn, 0);
    }
  };

  const makeClickShort = () => {
    const d = new Date();
    const DD = String(d.getDate()).padStart(2, '0');
    const MM = String(d.getMonth() + 1).padStart(2, '0');
    const YY = String(d.getFullYear()).slice(2);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds());
    const ms = String(d.getMilliseconds());
    return DD + MM + YY + hh + mm + ss + ms;
  };

  const siteDomainConst = window.location.host || '';

  const scriptURL = 'https://script.google.com/macros/s/AKfycbzpfADklTe8mxAB0mJHYes9Srkz4mHpWpl_JHJPZ6OV6_e63YEwqK2apDlndXFMZPmp/exec';

  const wpApiLink = document.querySelector('link[rel="https://api.w.org/"]');
  const wpJsonBase = (wpApiLink && wpApiLink.href)
    ? wpApiLink.href.replace(/\/?$/, '/')
    : (window.location.origin + '/wp-json/');
  const wpDbURL = wpJsonBase + 'jurnal/v1/waba';
  const wpSiteBase = wpJsonBase.replace(/wp-json\/?$/, '');
  const wpAjaxURL = wpSiteBase.replace(/\/?$/, '/') + 'wp-admin/admin-ajax.php';

  // Precompute browser/device once (avoid heavy UA regex on each click).
  const ua = navigator.userAgent || '';
  const ua4 = ua.substr(0, 4);
  const isMobileUA = (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(ua) ||
    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-([2-7])|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(ua4));
  const devicetypeConst = isMobileUA ? 'mobile' : 'desktop';
  const browserTypeConst = getBrowserType();

  // Avoid rebuilding URL object on every click.
  // URL object (new URL(...)) relatif mahal jika sering dibuat.
  // Karena click handler bisa sering terpanggil, kita cache berdasarkan href.
  let _cachedHref = '';
  let _cachedUrl = null;
  const getUrlObj = () => {
    const href = window.location.href;
    if (!_cachedUrl || href !== _cachedHref) {
      _cachedHref = href;
      _cachedUrl = new URL(href);
    }
    return _cachedUrl;
  };

  // Hoist regex/constants out of click path.
  // Regex ini dipakai untuk klasifikasi keyword/utm_term.
  // Kita letakkan di luar handler agar tidak dibuat ulang pada setiap klik.
  const keywordRegex = /(aplikasi|app|program|software|system|sistem|platform|paling\s|terbaik|terbagus|\bthe\b|best|berbasis web)/i;
  const keywordRegexNew = /(invoice online|laporan keuangan perusahaan|laporan keuangan|buat invoice online|cara membuat laporan keuangan|buat invoice|membuat invoice online|invoice template|invoice generator)/i;
  const kw_inventory = /(inventor|stok|stock|gudang|mrp|warehouse|wms|scm|sedia|inventar|barang|supply chain)/i;
  const kw_invoice = /(invoic|faktur|tagih|kwitansi|tempo|bon|nota)/i;
  const kw_invoice_exclude = /manu/i;
  const kw_manufacture = /(manufa|pabrik|produksi)/i;
  const kw_accounting = /(akutans|akuntans|accounti|laporan|report|keuangan|buku|bookkeeping|buku|ledger|catat)/i;
  const kw_erp = /(erp)/i;

  // Lazy DOM cache (in case waba() runs before the form is in DOM).
  // Beberapa halaman mungkin merender form hidden belakangan.
  // Kita cache referensi elemen DOM tapi tetap bisa re-query jika belum ada.
  let _domCache = null;
  const getDomCache = () => {
    if (_domCache && (_domCache.formWaba || _domCache.wabaRedirect)) {
      return _domCache;
    }

    const formWaba = document.querySelector('#form_shortener_gclid');
    _domCache = {
      wabaRedirect: document.querySelector('#waba_redirect'),
      formWaba,
      form: document.forms['form-shortener-gclid'] || null,
      fields: formWaba ? {
        f_gclid_short: document.querySelector('#f_gclid_short'),
        f_gclid: document.querySelector('#f_gclid'),
        f_device: document.querySelector('#f_device'),
        f_utmsource: document.querySelector('#f_utmsource'),
        f_utmmedium: document.querySelector('#f_utmmedium'),
        f_utmcampaign: document.querySelector('#f_utmcampaign'),
        f_utmterm: document.querySelector('#f_utmterm'),
        f_banner_id: document.querySelector('#f_banner_id'),
        f_gclid_journey: document.querySelector('#f_gclid_journey'),
        f_matchtype: document.querySelector('#f_matchtype'),
        f_journey: document.querySelector('#f_journey'),
        f_gaid: document.querySelector('#f_gaid'),
        f_waba_medium: document.querySelector('#f_waba_medium'),
        f_browser: document.querySelector('#f_browser'),
        f_referrer: document.querySelector('#f_referrer'),
        f_query_keyword: document.querySelector('#f_query_keyword'),
        f_visitor_id: document.querySelector('#f_visitor_id'),
        f_label: document.querySelector('#f_label'),
      } : null,
    };
    return _domCache;
  };

  const formDataToObject = (fd) => {
    // Konversi FormData menjadi object sederhana.
    // Dipakai untuk membuat payload tracking yang konsisten.
    const obj = {};
    fd.forEach((value, key) => {
      if (obj[key] === undefined) obj[key] = value;
    });
    return obj;
  };

  const buildDbPayload = (data) => {
    // Normalisasi payload yang akan dikirim ke DB.
    // Fungsi pick() mengambil value pertama yang tersedia dari beberapa kandidat key
    // (berguna karena nama field kadang berbeda antar template).
    const pick = (...keys) => {
      for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        const v = data && data[k] != null ? String(data[k]) : '';
        if (v !== '') return v;
      }
      return '';
    };

    return {
      short: pick('short', 'gclid_short', 'f_gclid_short'),
      gclid: pick('gclid', 'f_gclid'),
      journey: pick('journey', 'f_journey'),
      utm_term: pick('utm_term', 'f_utmterm'),
      matchtype: pick('matchtype', 'f_matchtype'),
      device: pick('device', 'f_device'),
      button_label_id: pick('button_label_id', 'label', 'f_label'),
      gaid: pick('gaid', 'f_gaid'),
      utm_source: pick('utm_source', 'f_utmsource'),
      utm_medium: pick('utm_medium', 'f_utmmedium'),
      utm_campaign: pick('utm_campaign', 'f_utmcampaign'),
      utm_content: pick('utm_content', 'f_utmcontent'),
      banner_id: pick('banner_id', 'f_banner_id'),
      waba_medium: pick('waba_medium', 'f_waba_medium'),
      browser: pick('browser', 'f_browser'),
      domain: pick('domain'),
      gclid_journey: pick('gclid_journey', 'f_gclid_journey'),
      // `referrer` should follow lead_referrer (attribution), with safe fallbacks.
      referrer: pick('referrer', 'f_referrer', 'lead_referrer', 'leads_referrer') || getCookie('lead_referrer') || leads_referrer || siteDomainConst,
      query_search_term: pick('query_search_term', 'query_keyword', 'f_query_keyword'),
      visitor_id: pick('visitor_id', 'f_visitor_id')
    };
  };

  const postToDb = async (form) => {
    const fd = new FormData(form);
    const payload = buildDbPayload(formDataToObject(fd));

    const res = await fetch(wpDbURL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
      keepalive: true
    });

    if (!res.ok) {
      throw new Error('DB endpoint HTTP ' + res.status);
    }

    const json = await res.json().catch(() => null);
    if (!json || json.ok !== true) {
      throw new Error('DB endpoint invalid response');
    }

    return json;
  };

  const postToGSheetsFromPayload = (payload) => {
    const fd = new FormData();
    fd.append('Short', payload.short || '');
    fd.append('GCLID', payload.gclid || '');
    fd.append('Journey', payload.journey || '');
    fd.append('UTM Term', payload.utm_term || '');
    fd.append('Matchtype', payload.matchtype || '');
    fd.append('Device', payload.device || '');
    fd.append('Button Label & Button ID', payload.button_label_id || '');
    fd.append('Button Journey', payload.button_label_id || '');
    fd.append('GAID', payload.gaid || '');
    fd.append('utm_source', payload.utm_source || '');
    fd.append('utm_medium', payload.utm_medium || '');
    fd.append('utm_campaign', payload.utm_campaign || '');
    fd.append('utm_content', payload.utm_content || '');
    fd.append('Banner ID', payload.banner_id || '');
    fd.append('Waba Medium', payload.waba_medium || '');
    fd.append('Browser', payload.browser || '');
    fd.append('Domain', payload.domain || window.location.host || '');
    fd.append('GCLID Journey', payload.gclid_journey || '');
    fd.append('Referrer', payload.referrer || siteDomainConst);
    fd.append('Query Search Term', payload.query_search_term || '');
    fd.append('Visitor ID', payload.visitor_id || '');

    return fetch(scriptURL, { method: 'POST', body: fd, keepalive: true });
  };

  const postToDbAjax = async (payload) => {
    // Endpoint DB via admin-ajax.
    // keepalive:true membantu agar request tetap terkirim walau user langsung pindah tab.
    const res = await fetch(wpAjaxURL + '?action=jurnal_waba_store', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
      keepalive: true
    });

    if (!res.ok) {
      throw new Error('admin-ajax HTTP ' + res.status);
    }

    const json = await res.json().catch(() => null);
    if (!json || json.success !== true) {
      const msg = json && json.data && json.data.message ? json.data.message : 'admin-ajax invalid response';
      throw new Error(msg);
    }

    return json;
  };

  const scheduleSendToDbThenFallback = (payload) => {
    // Tracking dilakukan di waktu idle agar tidak mengganggu respons klik.
    // Urutan:
    // 1) admin-ajax DB
    // 2) jika gagal -> GSheets
    const send = () => {
      // AJAX-only: skip REST (wp-json) to avoid 403 blocks.
      postToDbAjax(payload)
        .catch((errAjax) => {
          console.error('admin-ajax DB post failed, fallback to GSheets:', errAjax && errAjax.message ? errAjax.message : errAjax);
          return postToGSheetsFromPayload(payload)
            .then(response => {
              if (response && response.status == 200) {
                // no-op
              } else {
                console.error('GSheets response code : ', response && response.status ? response.status : 'unknown');
              }
            })
            .catch(err => {
              console.error('GSheets error!', err && err.message ? err.message : err);
            });
        });
    };

    // Defer network + JSON/FormData work off the click path (better INP).
    scheduleIdle(send);
  };

  // Ensure buttons become visible.
  // Di CSS theme, `.cta-waba` sering dibuat pointer-events:none sampai class `.show` ditambahkan.
  // Ini mencegah klik sebelum script siap.
  // NOTE: beberapa UI (mis. modal tab-grid) inject HTML via innerHTML setelah initial load,
  // jadi kita perlu juga menandai CTA yang muncul belakangan.
  const markWabaButtonsVisible = (root) => {
    try {
      if (!root) return;

      const markOne = (el) => {
        try {
          if (el && el.classList && el.classList.contains('cta-waba')) {
            el.classList.add('show');
          }
        } catch (e) {}
      };

      // Root itself might be the button.
      if (root.nodeType === 1) markOne(root);

      // And/or it may contain buttons.
      if (root.querySelectorAll) {
        root.querySelectorAll('.cta-waba').forEach(markOne);
      }
    } catch (e) {}
  };

  // Expose a manual refresh hook (optional for dynamic UIs).
  // Example usage: window.__jurnalWabaRefreshButtons(modalEl)
  if (typeof window.__jurnalWabaRefreshButtons !== 'function') {
    window.__jurnalWabaRefreshButtons = markWabaButtonsVisible;
  }

  markWabaButtonsVisible(document);

  // Auto-refresh when DOM changes (for dynamically injected content).
  if (!window.__jurnalWabaCtaObserverInstalled && typeof window.MutationObserver === 'function') {
    try {
      const pending = new Set();
      let scheduled = false;

      const flush = () => {
        scheduled = false;
        pending.forEach((node) => markWabaButtonsVisible(node));
        pending.clear();
      };

      const scheduleFlush = () => {
        if (scheduled) return;
        scheduled = true;
        // Use rAF so we run after DOM mutations batch.
        if (typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(flush);
        } else {
          setTimeout(flush, 0);
        }
      };

      const obs = new MutationObserver((mutations) => {
        for (let i = 0; i < mutations.length; i++) {
          const m = mutations[i];
          if (!m || !m.addedNodes) continue;
          for (let j = 0; j < m.addedNodes.length; j++) {
            const n = m.addedNodes[j];
            if (!n || n.nodeType !== 1) continue;
            // Only keep nodes that could contain .cta-waba to limit work.
            if (
              (n.classList && n.classList.contains('cta-waba')) ||
              (n.querySelector && n.querySelector('.cta-waba'))
            ) {
              pending.add(n);
              scheduleFlush();
            }
          }
        }
      });

      obs.observe(document.documentElement, { childList: true, subtree: true });
      window.__jurnalWabaCtaObserverInstalled = true;
    } catch (e) {
      // no-op
    }
  }

  const getCtaButtonFromEvent = (e) => {
    // Ambil elemen `.cta-waba` dari event:
    // - prioritas: target.closest('.cta-waba')
    // - fallback: e.composedPath() untuk kasus shadow DOM / event retargeting
    const rawTarget = e && e.target;
    const target = (rawTarget && rawTarget.nodeType === 3) ? rawTarget.parentElement : rawTarget;

    if (target && typeof target.closest === 'function') {
      const closest = target.closest('.cta-waba');
      if (closest) return closest;
    }

    if (e && typeof e.composedPath === 'function') {
      const path = e.composedPath();
      for (let i = 0; i < path.length; i++) {
        const node = path[i];
        if (node && node.classList && node.classList.contains('cta-waba')) return node;
      }
    }

    return null;
  };

  const clickHandler = (e) => {
    // Handler utama WABA.
    // Kriteria penting:
    // - Harus cepat (show loader + open tab terjadi saat user gesture)
    // - Harus aman dari double event
    // - Harus memblokir handler legacy yang mencoba redirect tab yang sama
    // Guard against multiple listeners handling the same event.
    if (e && e.__jurnalWabaHandled) return;

    if (window.__jurnalWabaDebug) console.log('WABA CTA clicked');

    const button = getCtaButtonFromEvent(e);
    if (!button) return;

    // Ignore synthetic/programmatic events (e.g. `el.click()` / `dispatchEvent`).
    // Those can re-trigger after a delay and cause popup-blocked fallbacks that
    // redirect the current tab.
    // Kenapa blok event sintetis itu penting?
    // - Ada script lain (global/legacy) yang kadang memanggil `element.click()` atau
    //   `dispatchEvent(new MouseEvent('click'))` setelah beberapa detik.
    // - Event seperti itu `isTrusted === false`.
    // - Jika kita izinkan, ia bisa memicu "fallback" popup-blocker dan akhirnya
    //   melakukan redirect di tab yang sama (bug yang kamu temui).
    try {
      if (e && typeof e.isTrusted === 'boolean' && e.isTrusted === false) {
        try { document.documentElement.dataset.jurnalWabaBlockedSynthetic = String(Date.now()); } catch (err) {}
        try { if (typeof e.preventDefault === 'function') e.preventDefault(); } catch (err) {}
        try { if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation(); } catch (err) {}
        try { if (typeof e.stopPropagation === 'function') e.stopPropagation(); } catch (err) {}
        return;
      }
    } catch (err) {}

    try {
      document.documentElement.dataset.jurnalWabaFired = String(Date.now());
    } catch (err) {}

    if (e) e.__jurnalWabaHandled = true;

    // Block legacy hidden-form submit (same-tab redirect) for a short time.
    // This is intentionally a bit longer than the delayed redirect reported.
    // Window block ini memberi waktu untuk menahan legacy script yang melakukan submit
    // (biasanya terjadi beberapa detik setelah klik).
    try { window.__jurnalBlockWabaFormSubmitUntil = Date.now() + 8000; } catch (err) {}

  // Stop legacy click handlers (some templates still attach inline listeners
  // that submit a form and redirect the current tab after a delay).
  // Kita lakukan preventDefault + stopPropagation di awal untuk memutus rantai handler lain.
  try { if (e && typeof e.preventDefault === 'function') e.preventDefault(); } catch (err) {}
  try { if (e && typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation(); } catch (err) {}
  try { if (e && typeof e.stopPropagation === 'function') e.stopPropagation(); } catch (err) {}

    // Show loader synchronously so it reliably appears even on slow CPU.
    // (Using requestAnimationFrame here can race with setTimeout work and never paint.)
    // Catatan: loader harus dipanggil sinkron (langsung), bukan menunggu raf,
    // karena kalau device berat, user tidak sempat melihat overlay dan akan klik lagi.
    const loadingShownAt = (window.performance && typeof window.performance.now === 'function')
      ? window.performance.now()
      : Date.now();

    showLoading();

    // Open a tab immediately (required to avoid popup blockers). We'll set the final URL right after.
    // Pola popup-blocker:
    // - window.open hanya diizinkan ketika masih dalam user gesture (handler klik).
    // - kita buka about:blank dulu, baru set lokasi setelah paint.
    const newTab = window.open('about:blank', '_blank');

    // ── SYNCHRONOUS: compute $url and navigate the new tab ──────────────────
    // Safari iOS revokes the gesture token after any async boundary (rAF/setTimeout).
    // All ops below are fast (string/cookie reads) so they are safe to run
    // synchronously within the user-gesture stack.
    const url = getUrlObj();
    const sp = url.searchParams;

    const cookieCache = new Map();
    const gc = (key) => {
      if (cookieCache.has(key)) return cookieCache.get(key);
      const val = getCookie(key) || '';
      cookieCache.set(key, val);
      return val;
    };

    const browserType = browserTypeConst;
    const click_sh = makeClickShort();
    const gclid = sp.get('gclid') || gc('gclid');
    const msclkid = sp.get('msclkid') || gc('msclkid');
    const umedium = sp.get('utm_medium') || gc('utm_medium');
    const ucampaign = sp.get('utm_campaign') || gc('utm_campaign');
    const usource = sp.get('utm_source') || gc('utm_source');
    const utm_term = sp.get('utm_term') || gc('utm_term');
    const matchtype = sp.get('matchtype') || gc('matchtype');
    const query_keyword = sp.get('query') || gc('query_keyword') || 'null';
    const devicetype = devicetypeConst;
    const banner_journey = gc('banner_journey');
    const cta_journey = gc('cta_journey');
    const cta_last_click = gc('cta_last_click');
    const medium_code = gc('waba_code');

    let gclidmsclkid = '';
    if (gclid || msclkid) {
      gclidmsclkid = text_truncate(gclid + '|' + msclkid, 250, '');
    }

    let $url = button.getAttribute('href') || '';
    // Normalize href coming from various renderers (esc_url / HTML attributes).
    // - `&amp;` => `&`
    // - `%7B%7Bcode%7D%7D` => `{{code}}`
    $url = String($url)
      .replace(/&amp;/g, '&')
      .replace(/%7B%7Bcode%7D%7D/ig, '{{code}}');

    if ($url) {
      // Fallback: if placeholder is missing but URL has a WhatsApp text parameter,
      // inject the request code at the start of the message.
      if ($url.indexOf('{{code}}') === -1) {
        try {
          if ($url.indexOf('text=') !== -1) {
            $url = $url.replace(/(text=)/i, '$1[Req.J' + click_sh + '] - ');
          }
        } catch (e) {}
      }

      if (utm_term != "") {
          if (keywordRegex.test(utm_term) || keywordRegexNew.test(utm_term)) {
              var kw = "";

              if (kw_inventory.test(utm_term)) {
                  kw = "'Inventory' ";
              }

              if (kw_invoice.test(utm_term)) {
                  if (!kw_invoice_exclude.test(utm_term)) {
                      kw = "'Invoice' ";
                  }
              }

              if (kw_manufacture.test(utm_term)) {
                  kw = "'Manufacture' ";
              }

              if (kw_accounting.test(utm_term)) {
                  kw = "'Accounting' ";
              }

              if (kw_erp.test(utm_term)) {
                  kw = "'ERP' ";
              }

              var $url_ = $url.split("{{code}}");
              if (
                  $url.includes(
                      "tertarik dengan fitur Mekari Jurnal"
                  )
              ) {
                  $url =
                      $url_[0] +
                      "[Req.J" +
                      click_sh +
                      "] - Halo, Saya tertarik dengan fitur " +
                      kw +
                      "Mekari Jurnal";
              } else if (
                  $url.includes("ask more about Mekari Jurnal")
              ) {
                  $url =
                      $url_[0] +
                      "[Req.J" +
                      click_sh +
                      "] - Hello, I want to ask more about " +
                      kw +
                      "Mekari Jurnal";
              } else if (
                  $url.includes("interested with Mekari Jurnal")
              ) {
                  $url =
                      $url_[0] +
                      "[Req.J" +
                      click_sh +
                      "] - Hello, I'm interested with " +
                      kw +
                      " Mekari Jurnal";
              } else {
                  if ($url.includes("{{code}} -")) {
                      $url = $url.replace(
                          "{{code}}",
                          "[Req.J" + click_sh + "]"
                      );
                  } else if ($url.includes("{{code}}%20-%20")) {
                      $url = $url.replace(
                          "{{code}}",
                          "[Req.J" + click_sh + "]"
                      );
                  } else {
                      $url = $url.replace(
                          "{{code}}",
                          "[Req.J" + click_sh + "] - "
                      );
                  }
              }
          } else {
              if ($url.includes("{{code}} -")) {
                  $url = $url.replace(
                      "{{code}}",
                      "[Req.J" + click_sh + "]"
                  );
              } else if ($url.includes("{{code}}%20-%20")) {
                  $url = $url.replace(
                      "{{code}}",
                      "[Req.J" + click_sh + "]"
                  );
              } else {
                  $url = $url.replace(
                      "{{code}}",
                      "[Req.J" + click_sh + "] - "
                  );
              }
          }
      } else {
          if ($url.includes("{{code}} -")) {
              $url = $url.replace(
                  "{{code}}",
                  "[Req.J" + click_sh + "]"
              );
          } else if ($url.includes("{{code}}%20-%20")) {
              $url = $url.replace(
                  "{{code}}",
                  "[Req.J" + click_sh + "]"
              );
          } else {
              $url = $url.replace(
                  "{{code}}",
                  "[Req.J" + click_sh + "] - "
              );
          }
      }
    }

    if($url.includes('{{suff}}')) {
      $url = $url.replace('{{suff}}', '');
    } else if($url.includes('{{suf}}')) {
      $url = $url.replace('{{suf}}', '');
    } else if($url.includes('{{suff1}}')) {
      $url = $url.replace('{{suff1}}', '');
    } else if($url.includes('{{suff2}}')) {
      $url = $url.replace('{{suff2}}', '');
    } else if($url.includes('{{suff3}}')) {
      $url = $url.replace('{{suff3}}', '');
    }

    $url += "&app_absent=0";

    // Navigate the new tab synchronously — must happen while gesture token is still
    // valid (Safari iOS blocks cross-origin href assignment after any async boundary).
    if (newTab && typeof newTab.location !== 'undefined') {
      try { newTab.location.href = $url; } catch (e2) {
        // Safari may throw a SecurityError for about:blank location writes in rare
        // configurations; fall back to same-tab navigation.
        window.location.href = $url;
      }
    } else {
      // Popup was blocked — navigate current tab as last resort.
      window.location.href = $url;
    }

    // ── DEFERRED: tracking, DOM updates, loader hide ─────────────────────────
    // Navigation is already underway; now defer the non-critical work so it
    // doesn't block the paint / INP.
    scheduleAfterPaint(() => {
      // Update redirect field if present (cheap single write).
      const dom = getDomCache();
      if (dom && dom.wabaRedirect) {
        dom.wabaRedirect.value = $url;
      }

      // Keep loader visible for at least a short moment for perceived feedback on slow devices.
      const now = (window.performance && typeof window.performance.now === 'function')
        ? window.performance.now()
        : Date.now();
      const minVisibleMs = 3000;
      const elapsed = Math.max(0, now - loadingShownAt);
      const remaining = Math.max(0, minVisibleMs - elapsed);
      setTimeout(() => {
        hideLoading();
      }, remaining);

      // Build explicit payload for DB; send in idle time.
      const payload = {
        short: click_sh,
        gclid: gclidmsclkid,
        journey: (gc('user_journey') ? text_truncate(gc('user_journey'), 250, '') : ''),
        utm_term: utm_term,
        matchtype: matchtype,
        device: (devicetype == 'desktop') ? 'Desktop' : 'Mobile',
        button_label_id: (() => {
          let label = '';
          if (button.classList.contains('ic-wa')) {
            label = 'ID: ' + (button.id || '') + ', Label: Sticky WABA';
          } else if (button.classList.contains('top-banner-sticky') || button.classList.contains('top-banner-bot')) {
            label = 'ID: ' + (button.id || '') + ', Label: Sticky Banner';
          } else if (button.id) {
            label = 'ID: ' + button.id + ', Label: ' + (button.textContent || '');
          } else {
            label = 'ID: , Label: ' + (button.textContent || '');
          }

          let cta_journey_url = '';
          if (cta_journey) {
            if (cta_last_click) {
              cta_journey_url = (cta_last_click !== (button.id || '')) ? (cta_journey + '|' + label) : cta_journey;
            } else {
              cta_journey_url = label;
            }
          } else {
            cta_journey_url = label;
          }

          // Keep cookie tracking behavior.
          if (cta_journey) {
            updateCookie('cta_journey', cta_journey_url);
          } else {
            setCookie('cta_journey', cta_journey_url, 31);
          }

          if (cta_last_click) {
            updateCookie('cta_last_click', button.id || '');
          } else {
            setCookie('cta_last_click', button.id || '', 31);
          }

          return cta_journey_url;
        })(),
        gaid: (gc('gaid') ? gc('gaid') : (gc('_ga') ? gc('_ga').slice(6) : '')),
        utm_source: (usource === 'chatgpt.com') ? 'website' : usource,
        utm_medium: umedium,
        utm_campaign: ucampaign,
        utm_content: (sp.get('utm_content') || gc('utm_content') || ''),
        banner_id: banner_journey || '',
        waba_medium: medium_code || '',
        browser: browserType,
        domain: window.location.host || '',
        gclid_journey: (gc('gclid_journey') ? gc('gclid_journey') : ''),
        referrer: (gc('lead_referrer') || gc('leads_referrer') || leads_referrer || siteDomainConst),
        query_search_term: query_keyword,
        visitor_id: (gc('visitor_id') || '')
      };

      scheduleSendToDbThenFallback(payload);
    });
  };

  // Bind on window first (earliest in event path), plus a document fallback.
  // Listen to multiple event types because some mobile stacks trigger navigation
  // on touch/pointer events and suppress the subsequent click.
  let _lastHandledTs = 0;
  let _lastHandledEl = null;
  const dedupedHandler = (e) => {
    const button = getCtaButtonFromEvent(e);
    if (!button) return;

    const now = (window.performance && typeof window.performance.now === 'function')
      ? window.performance.now()
      : Date.now();

    // Dedupe across pointer/touch/click chains.
    if (_lastHandledEl === button && (now - _lastHandledTs) < 900) {
      // Important: when a synthetic click follows touch/pointer, we must still
      // prevent default navigation in the current tab.
      try { if (e) e.__jurnalWabaHandled = true; } catch (err) {}
      try { if (e && typeof e.preventDefault === 'function') e.preventDefault(); } catch (err) {}
      try { if (e && typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation(); } catch (err) {}
      try { if (e && typeof e.stopPropagation === 'function') e.stopPropagation(); } catch (err) {}
      return;
    }
    _lastHandledEl = button;
    _lastHandledTs = now;

    clickHandler(e);
  };

  const bind = (target, type) => {
    try {
      target.addEventListener(type, dedupedHandler, { passive: false, capture: true });
    } catch (e) {}
  };

  ['click', 'pointerup', 'touchend'].forEach((type) => {
    bind(window, type);
    bind(document, type);
  });

  // Mark handler attached only after we actually bind.
  window.__jurnalWabaHandlerAttached = true;
  window.__jurnalWabaBound = true;

  try {
    document.documentElement.dataset.jurnalWabaBound = '1';
  } catch (err) {}

  // untuk memanggil function generate campaign info setelah waba code diperoleh

  // if(is_sr == "false") {
  //   setCampaignInfo();
  // }
}